import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Play, Pause, Radio, RefreshCw } from 'lucide-react';
import { fetchLocalNews as fetchLocalNewsHandler } from './localNewsHandler';
import { SecureAPI } from '../config.js';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
const VOICE_CONFIG = {
  guide: '33045fd9-8010-43f6-b6b0-da3fbf326c29',
  therapist: '41745808-6790-4be4-83db-363e20db7e71',
  guest1: '5bb7de05-c8fe-426a-8fcc-ba4fc4ce9f9c',
  guest2: 'c01854384-4e4e-48d4-90d1-b22f760a58b5'
};

const INTRO_MUSIC_OPTIONS = [
  '/music/intro-outro-19.mp3',
  '/music/intro-outro-18.mp3',
  '/music/intro-outro-11.mp3',
  '/music/intro-outro-12.mp3',
  '/music/intro-outro-16.mp3'
];

const INTERMISSION_MUSIC_OPTIONS = [
  '/music/intro-outro-15.mp3',
  '/music/intro-outro-16.mp3',
  '/music/intro-outro-20.mp3',
  '/music/intro-outro-17.mp3',
  '/music/intro-outro-6.mp3'
];

const LocalNewsStation = ({ userName, userLocation, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [totalNewsCount, setTotalNewsCount] = useState(0);
  const [newsSources, setNewsSources] = useState([]);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [hasGuest, setHasGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [showArticleConfirm, setShowArticleConfirm] = useState(false);
const [selectedArticle, setSelectedArticle] = useState(null);
  const shouldStopRef = useRef(false);
  const isPausedRef = useRef(false);
  const currentAudioRef = useRef(null);
  const audioQueueRef = useRef(new Map());
  const playbackSpeedRef = useRef(playbackSpeed);
  const scriptLinesRef = useRef([]);
  const newsSegmentsRef = useRef([]);
  const preGeneratedRef = useRef(new Set());
  const transcriptEndRef = useRef(null);
  const [newsCards, setNewsCards] = useState([]);
const [currentCardIndex, setCurrentCardIndex] = useState(0);
const [isLoadingCards, setIsLoadingCards] = useState(true);
const newsCardsRef = useRef(null);
const intermissionAudioRef = useRef(null);
const [isSearchingComplete, setIsSearchingComplete] = useState(false);
const [completedCards, setCompletedCards] = useState(new Set());
const completedCardsRef = useRef(new Set());
const [isFetchingNewCycle, setIsFetchingNewCycle] = useState(false);
const [isNavigating, setIsNavigating] = useState(false);
const isNavigatingRef = useRef(false);
const [heardNewsIds, setHeardNewsIds] = useState(new Set());
const heardNewsIdsRef = useRef(new Set());
const [currentDaysBack, setCurrentDaysBack] = useState(0);
const currentDaysBackRef = useRef(0);
const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
const activeAudioElements = useRef(new Set()); // ✅ Track ALL audio elements
const preloadAbortControllers = useRef(new Map()); // ✅ For SelectedNewsStation
const lastNavigationTimeRef = useRef(0); // ✅ For cooldown
const NAVIGATION_COOLDOWN = 10000; // ✅ 10 seconds

// ============================================
// 📱 CAPACITOR NOTIFICATION HELPERS
// ============================================

// ============================================
// 📱 CAPACITOR NOTIFICATION HELPERS
// ============================================

const requestLocalNotificationPermission = async () => {
  if (!Capacitor.isNativePlatform()) {
    log('ℹ️ Not on native platform - notifications available on mobile');
    return true;
  }

  try {
    const permission = await LocalNotifications.requestPermissions();
    const granted = permission.display === 'granted';
    log(`📱 Notification permission: ${permission.display}`);
    return granted;
  } catch (error) {
    log(`⚠️ Error requesting permission: ${error.message}`);
    return false;
  }
};

const sendLocalNewsNotification = async (userName, city, state) => {
  if (!Capacitor.isNativePlatform()) {
    log('ℹ️ Notifications only work on mobile');
    return null;
  }

  try {
    const notificationId = Math.floor(Math.random() * 100000);

    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: `📻 ${city} News Ready`,
          body: `Hey ${userName}, your local news update is ready to listen!`,
          schedule: {
            at: new Date(Date.now() + 1000) // Send immediately
          },
          sound: 'default',
          smallIcon: 'ic_notification',
          iconColor: '#667eea',
          largeBody: `Tap to open your ${city} local news station`,
          extra: {
            type: 'localNews',
            city: city,
            state: state,
            timestamp: Date.now()
          }
        }
      ]
    });

    log(`✅ Notification sent: "${city} News Ready"`);
    return notificationId;
  } catch (error) {
    log(`⚠️ Failed to send notification: ${error.message}`);
    return null;
  }
};

const scheduleLocalNewsDailyNotification = async (userName, city, state, hasUsedBefore) => {
  try {
    if (!hasUsedBefore) {
      log('ℹ️ First time using local news - no notification needed');
      return false;
    }

    // ✅ RANDOMIZED INTERVALS: 1-3 days unpredictably
    const lastNotificationKey = `lastLocalNewsNotification_${city}_${state}`;
    const lastNotificationTime = parseInt(localStorage.getItem(lastNotificationKey) || 0);
    const now = Date.now();

    // ✅ Pick random interval: 1, 2, or 3 days
    const randomIntervals = [
      1 * 24 * 60 * 60 * 1000,  // 1 day
      2 * 24 * 60 * 60 * 1000,  // 2 days
      3 * 24 * 60 * 60 * 1000   // 3 days
    ];
    const randomInterval = randomIntervals[Math.floor(Math.random() * randomIntervals.length)];
    const daysSince = Math.floor((now - lastNotificationTime) / (24 * 60 * 60 * 1000));

    if (lastNotificationTime > 0 && now - lastNotificationTime < randomInterval) {
      log(`ℹ️ Too soon - last notification was ${daysSince} day(s) ago, waiting for ${randomInterval / (24 * 60 * 60 * 1000)} day interval`);
      return false;
    }

    // ✅ Request permission if needed
    const hasPermission = await requestLocalNotificationPermission();
    if (!hasPermission) {
      log('⚠️ Notifications disabled by user');
      return false;
    }

    // ✅ Send the notification
    const notificationId = await sendLocalNewsNotification(userName, city, state);

    if (notificationId) {
      // ✅ Store timestamp of this notification
      localStorage.setItem(lastNotificationKey, now.toString());
      const nextIntervalDays = randomInterval / (24 * 60 * 60 * 1000);
      log(`✅ Notification sent! Next one in ${nextIntervalDays} day(s)`);
      return true;
    }

    return false;
  } catch (error) {
    log(`⚠️ Notification schedule error: ${error.message}`);
    return false;
  }
};

const checkUserHasUsedLocalNews = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready to check user history');
      return false;
    }

    const userData = await window.firestoreService.getUserData();

    // ✅ Check multiple indicators of past usage
    const hasLocalNewsSessions = userData?.localNewsSessions && Object.keys(userData.localNewsSessions).length > 0;
    const hasNewsSession = userData?.localNewsSession?.timestamp;
    const hasNewsPreferences = userData?.localNewsPreferences?.hasUsedLocalNews;

    const hasUsed = hasLocalNewsSessions || hasNewsSession || hasNewsPreferences;

    log(`📊 User history: ${hasUsed ? 'HAS USED' : 'FIRST TIME'} local news`);
    return hasUsed;
  } catch (error) {
    log(`⚠️ Failed to check user history: ${error.message}`);
    return false;
  }
};

const markUserUsedLocalNews = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Cannot mark user as having used local news - Firestore not ready');
      return;
    }

    const userData = await window.firestoreService.getUserData();

    // ✅ Mark that they've used local news
    await window.firestoreService.updateUserData({
      localNewsPreferences: {
        ...userData?.localNewsPreferences,
        hasUsedLocalNews: true,
        firstUseDate: userData?.localNewsPreferences?.firstUseDate || new Date().toISOString(),
        lastUseDate: new Date().toISOString(),
        enableNotifications: true
      }
    });

    log('✅ Marked user as having used local news');
  } catch (error) {
    log(`⚠️ Failed to mark user usage: ${error.message}`);
  }
};

useEffect(() => {
  // ✅ INITIALIZATION - Check user history and send notification if needed
  const initializeNotifications = async () => {
    try {
      log('🚀 Initializing local news notification system...');

      // ✅ Listen for notification taps
      if (Capacitor.isNativePlatform()) {
        try {
          LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
            log(`📻 Notification tapped: ${notification.notification.title}`);
            // User tapped notification - they'll see the station UI
          });
        } catch (e) {
          log(`⚠️ Could not set up notification listener: ${e.message}`);
        }
      }

      // ✅ STEP 1: Check if user has used local news before
      const hasUsedBefore = await checkUserHasUsedLocalNews();

      if (hasUsedBefore) {
        log('✅ User has used local news before - enabling notifications');

        // ✅ STEP 2: Check and send daily notification
        await scheduleLocalNewsDailyNotification(
          userName,
          userLocation.city,
          userLocation.state,
          hasUsedBefore
        );
      } else {
        log('ℹ️ First time using local news - will notify after first session');
      }
    } catch (error) {
      log(`⚠️ Notification initialization error: ${error.message}`);
    }
  };

  // ✅ Run initialization after a short delay (allow Firestore to settle)
  const initTimer = setTimeout(initializeNotifications, 2000);

  // ✅ CLEANUP
  return () => {
    clearTimeout(initTimer);
    log('🧹 Component unmounting - cleanup');
    shouldStopRef.current = true;
    isPausedRef.current = true;

    // Abort all preloads
    if (preloadAbortControllers.current) {
      preloadAbortControllers.current.forEach(controller => {
        try {
          controller.abort();
        } catch (e) {}
      });
      preloadAbortControllers.current.clear();
    }

    // Stop all tracked audio
    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
      } catch (e) {}
    });
    activeAudioElements.current.clear();

    // Clean up refs
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current = null;
    }

    // Clean up cache
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      audioQueueRef.current.clear();
    }
  };
}, [userName, userLocation.city, userLocation.state]);
const nuclearCleanupAudio = async () => {
  log(`🔴 NUCLEAR CLEANUP - Stopping ${activeAudioElements.current.size} audio elements`);
  
  // ✅ ABORT ALL PRELOAD REQUESTS (if using preloads)
  if (preloadAbortControllers.current) {
    preloadAbortControllers.current.forEach((controller, key) => {
      try {
        controller.abort();
        log(`🛑 Aborted preload: ${key}`);
      } catch (e) {}
    });
    preloadAbortControllers.current.clear();
  }
  
  // ✅ STOP ALL TRACKED AUDIO ELEMENTS
  activeAudioElements.current.forEach(audio => {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
    } catch (e) {}
  });
  activeAudioElements.current.clear();
  
  // ✅ Clear main refs
  currentAudioRef.current = null;
  
  // ✅ Clear intermission ref
  if (intermissionAudioRef.current) {
    try {
      const audio = intermissionAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
      intermissionAudioRef.current = null;
    } catch (e) {
      intermissionAudioRef.current = null;
    }
  }
  
  // ✅ Clear audio queue and revoke URLs (if exists)
  if (audioQueueRef.current) {
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
  }
  
  // ✅ Wait for complete cleanup
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  log(`✅ Nuclear cleanup complete - ${activeAudioElements.current.size} audio elements remaining`);
};
const handleRefreshClick = () => {
  setShowRefreshConfirm(true);
};

const cancelRefresh = () => {
  setShowRefreshConfirm(false);
};

const confirmRefresh = async () => {
  setShowRefreshConfirm(false);
  
  if (!isPlaying) return;
  
  log('🔄 User requested refresh - searching for new news...');
  
  // ✅ Stop current playback
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  await nuclearStopAllAudio();
  await new Promise(resolve => setTimeout(resolve, 600));
  
  let attempts = 0;
  const maxAttempts = 8;
  while ((currentAudioRef.current !== null || intermissionAudioRef.current !== null) && attempts < maxAttempts) {
    await nuclearStopAllAudio();
    await new Promise(resolve => setTimeout(resolve, 200));
    attempts++;
  }
  
  currentAudioRef.current = null;
  intermissionAudioRef.current = null;
  
  // ✅ Play transition audio
  const transitionPhrases = [
    `Alright ${userName}, let's switch gears and search for fresh news.`,
    `${userName}, let's find some new stories for you.`,
    `Okay ${userName}, searching for the latest updates.`,
    `${userName}, let me grab some fresh news for you.`,
  ];
  
  const randomPhrase = transitionPhrases[Math.floor(Math.random() * transitionPhrases.length)];
  
  shouldStopRef.current = false;
  isPausedRef.current = false;
  
  await playAudio(randomPhrase, Math.random() < 0.5 ? 'guide' : 'therapist');
  
  // ✅ Show searching state
  setTranscript([{
    voice: 'system',
    text: 'Searching for fresh local news...',
    timestamp: Date.now(),
    isSearching: true
  }]);
  
  setCurrentStatus('Gathering local news...');
  setIsLoadingCards(true);
  setIsFetchingNewCycle(true);
  
  // ✅ Clear completed cards and start new cycle
  completedCardsRef.current.clear();
  setCompletedCards(new Set());
  setCurrentCardIndex(0);
  
  // ✅ Jump directly to runNewsCycle
  await runNewsCycle();
};
const handleNextCard = async () => {
  // ✅ COOLDOWN CHECK
  const now = Date.now();
  if (now - lastNavigationTimeRef.current < NAVIGATION_COOLDOWN) {
    const remaining = Math.ceil((NAVIGATION_COOLDOWN - (now - lastNavigationTimeRef.current)) / 1000);
    log(`⏳ Please wait ${remaining} seconds before navigating`);
    return;
  }

  if (isNavigatingRef.current) {
    log('⚠️ Navigation already in progress');
    return;
  }

  const nextIndex = currentCardIndex + 1;
  if (nextIndex >= newsCards.length || !isPlaying) return;

  // ✅ SET COOLDOWN
  lastNavigationTimeRef.current = now;
  isNavigatingRef.current = true;
  setIsNavigating(true);

  log(`⏭️ Skipping to next card ${currentCardIndex} → ${nextIndex}`);

  // ✅ STEP 1: Set stop flags
  shouldStopRef.current = true;
  isPausedRef.current = true;

  // ✅ Mark current card as completed
  const currentCard = newsCards[currentCardIndex];
  if (currentCard) {
    completedCardsRef.current.add(currentCard.id);
    setCompletedCards(new Set(completedCardsRef.current));
  }

  // ✅ STEP 2: NUCLEAR CLEANUP
  await nuclearCleanupAudio();

  // ✅ STEP 3: Wait for silence
  await new Promise(resolve => setTimeout(resolve, 1000));

  // ✅ STEP 4: Verify cleanup
  if (activeAudioElements.current.size > 0) {
    log('⚠️ Zombie audio detected! Running cleanup again...');
    await nuclearCleanupAudio();
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  // ✅ STEP 5: Update UI
  setCurrentCardIndex(nextIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === nextIndex,
    isReady: idx <= nextIndex
  })));

  if (newsCardsRef.current?.children[nextIndex]) {
    newsCardsRef.current.children[nextIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  // ✅ STEP 6: Clear stop flags
  shouldStopRef.current = false;
  isPausedRef.current = false;

  // ✅ STEP 7: Transition phrase
  const nextCard = newsCards[nextIndex];
  const transitionPhrases = [
    `Alright, so this next one is about ${nextCard.source}.`,
    `Next up, we're looking at something from ${nextCard.source}.`,
    `Moving on, here's one from ${nextCard.source}.`,
  ];
  
  const randomPhrase = transitionPhrases[Math.floor(Math.random() * transitionPhrases.length)];
  await playAudio(randomPhrase, Math.random() < 0.5 ? 'guide' : 'therapist');

  // ✅ STEP 8: Calculate resume point
  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = nextIndex * linesPerNews;

  // ✅ STEP 9: Safety check and resume
  await new Promise(resolve => setTimeout(resolve, 300));

  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
  }
};
const handlePrevCard = async () => {
  // ✅ COOLDOWN CHECK
  const now = Date.now();
  if (now - lastNavigationTimeRef.current < NAVIGATION_COOLDOWN) {
    const remaining = Math.ceil((NAVIGATION_COOLDOWN - (now - lastNavigationTimeRef.current)) / 1000);
    log(`⏳ Please wait ${remaining} seconds before navigating`);
    return;
  }

  if (isNavigatingRef.current) {
    log('⚠️ Navigation already in progress');
    return;
  }
  
  const prevIndex = currentCardIndex - 1;
  if (prevIndex < 0 || !isPlaying) return;
  
  // ✅ SET COOLDOWN
  lastNavigationTimeRef.current = now;
  isNavigatingRef.current = true;
  setIsNavigating(true);
  
  log(`⏮️ Going back to card ${currentCardIndex} → ${prevIndex}`);
  
  // ✅ STEP 1: Set stop flags
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  // ✅ STEP 2: NUCLEAR CLEANUP
  await nuclearCleanupAudio();
  
  // ✅ STEP 3: Wait for silence
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // ✅ STEP 4: Verify cleanup
  if (activeAudioElements.current.size > 0) {
    log('⚠️ Zombie audio detected! Running cleanup again...');
    await nuclearCleanupAudio();
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  // ✅ STEP 5: Update UI
  setCurrentCardIndex(prevIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === prevIndex,
    isReady: idx <= prevIndex
  })));
  
  if (newsCardsRef.current?.children[prevIndex]) {
    newsCardsRef.current.children[prevIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }
  
  // ✅ STEP 6: Clear stop flags
  shouldStopRef.current = false;
  isPausedRef.current = false;
  
  // ✅ STEP 7: Transition phrase
  const prevCard = newsCards[prevIndex];
  const transitionPhrases = [
    `Alright, going back to ${prevCard.source}.`,
    `Let's revisit this one from ${prevCard.source}.`,
    `Going back to this story from ${prevCard.source}.`,
  ];
  
  const randomPhrase = transitionPhrases[Math.floor(Math.random() * transitionPhrases.length)];
  await playAudio(randomPhrase, Math.random() < 0.5 ? 'guide' : 'therapist');
  
  // ✅ STEP 8: Calculate resume point
  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = prevIndex * linesPerNews;
  
  // ✅ STEP 9: Safety check and resume
  await new Promise(resolve => setTimeout(resolve, 300));
  
  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
  }
};

const saveLocalNewsSession = async (cardIndex, scriptLineIndex, completedCardIds) => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready, skipping session save');
      return;
    }

    const sessionData = {
      lastCardIndex: cardIndex,
      lastLineIndex: scriptLineIndex,
      completedCards: Array.from(completedCardIds),
      hasGuest: hasGuest,
      guestName: guestName,
      currentCycle: currentCycle,
      heardNewsIds: Array.from(heardNewsIdsRef.current),
      currentDaysBack: currentDaysBackRef.current,
      newsCards: newsCards.map(card => ({
        id: card.id,
        articleId: card.articleId,
        title: card.title,
        fullTitle: card.fullTitle,
        image: card.image,
        source: card.source,
        url: card.url,
        time: card.time,
        snippet: card.snippet
      })),
      timestamp: Date.now(),
      date: new Date().toDateString(),
      location: userLocation
    };

    await window.firestoreService.saveLocalNewsSession(sessionData);
    
    log(`✅ Session saved: card ${cardIndex}/5, heard: ${heardNewsIdsRef.current.size} articles`);
  } catch (error) {
    log(`⚠️ Could not save session: ${error.message}`);
  }
};

const getLocalNewsSession = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready, skipping session load');
      return null;
    }

    const data = await window.firestoreService.getLocalNewsSession();
    
    if (data && data.date === new Date().toDateString()) {
      log(`📍 Found session: card ${data.lastCardIndex}, ${data.completedCards.length} completed`);
      return data;
    } else if (data) {
      log('🗑️ Old session found, starting fresh');
      await clearLocalNewsSession();
    }
  } catch (error) {
    log(`⚠️ Could not load session: ${error.message}`);
  }
  return null;
};

const clearLocalNewsSession = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready');
      return;
    }

    await window.firestoreService.clearLocalNewsSession();
    log('🗑️ Session cleared');
  } catch (error) {
    log(`⚠️ Could not clear session: ${error.message}`);
  }
};
const handleArticleClick = (card) => {
  setSelectedArticle(card);
  setShowArticleConfirm(true);
};

const cancelArticle = () => {
  setShowArticleConfirm(false);
  setSelectedArticle(null);
};

const confirmArticle = () => {
  if (selectedArticle?.url) {
    window.open(selectedArticle.url, '_blank');
  }
  setShowArticleConfirm(false);
  setSelectedArticle(null);
};
const runNewsCycle = async () => {
  try {
    log(`\n🔄 Starting news cycle ${currentCycle + 1}`);
    setCurrentStatus('Gathering local news...');
    setTranscript([{
      voice: 'system',
      text: 'Searching for fresh local news...',
      timestamp: Date.now(),
      isSearching: true
    }]);
    
    log('🎵 Starting intermission music...');
    
    setIsLoadingCards(true);
    setIsSearchingComplete(true);
    setIsFetchingNewCycle(true);
    
    // ✅ Start intermission music
    const musicPromise = playIntermissionMusicWithFade();
    
    const musicStartTime = Date.now();
    const minMusicDuration = 6000;
    
    // ✅ Fetch news and generate script in parallel with music
    let news = [];
    const MAX_DAYS_BACK = 14;
    
    log('📡 Step 1: Checking TODAY for fresh articles...');
    news = await fetchNews(0);
    
    if (news.length >= 5) {
      log('✅ Found 5 fresh articles TODAY!');
      currentDaysBackRef.current = 0;
      setCurrentDaysBack(0);
    } else {
      log(`⚠️ Only ${news.length} unheard articles today, need to backtrack...`);
      
      let daysBack = currentDaysBackRef.current === 0 ? 1 : currentDaysBackRef.current;
      let attempts = 0;
      
      while (news.length < 5 && daysBack <= MAX_DAYS_BACK && attempts < MAX_DAYS_BACK) {
        log(`📡 Step 2: Checking ${daysBack} day${daysBack > 1 ? 's' : ''} back...`);
        
        const olderNews = await fetchNews(daysBack);
        
        if (olderNews.length > 0) {
          log(`✅ Found ${olderNews.length} unheard articles from ${daysBack} days back`);
          
          news = [...news, ...olderNews].slice(0, 5);
          
          currentDaysBackRef.current = daysBack;
          setCurrentDaysBack(daysBack);
          
          if (news.length >= 5) {
            log(`✅ Got 5 articles!`);
            break;
          }
        } else {
          log(`⚠️ No unheard news at ${daysBack} days back`);
        }
        
        daysBack++;
        attempts++;
      }
      
      if (news.length < 5) {
        log('🆘 Reached 2 weeks back with < 5 articles. Clearing heard news and restarting...');
        
        heardNewsIdsRef.current.clear();
        setHeardNewsIds(new Set());
        currentDaysBackRef.current = 0;
        setCurrentDaysBack(0);
        
        news = await fetchNews(0);
        
        if (news.length === 0) {
          log('❌ Still no news found after clearing. Waiting 30 seconds...');
          await musicPromise; // Stop music
          await nuclearStopAllAudio();
          await new Promise(resolve => setTimeout(resolve, 30000));
          return runNewsCycle();
        }
      }
    }
    
    log(`✅ Final selection: ${news.length} articles ready to broadcast`);
    
    // ✅ PRE-GENERATE SCRIPT while music plays
    log('📝 Pre-generating script during intermission music...');
    
    const includeGuest = shouldIncludeGuest(currentCycle);
    const guest = includeGuest ? selectRandomGuest() : null;
    
    if (includeGuest && guest) {
      log(`🎙️ Guest joining: ${guest.name}`);
      setHasGuest(true);
      setGuestName(guest.name);
    } else {
      setHasGuest(false);
      setGuestName('');
    }
    
    const scriptPromise = generateStationScript(news, includeGuest, guest?.name || '', false);
    
    // ✅ Wait for minimum music duration
    const elapsedTime = Date.now() - musicStartTime;
    if (elapsedTime < minMusicDuration) {
      const remainingTime = minMusicDuration - elapsedTime;
      log(`⏳ Waiting ${remainingTime}ms for minimum music duration...`);
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }
    
    // ✅ Wait for script to finish generating
    const script = await scriptPromise;
    
    if (shouldStopRef.current) return;
    
    log('✅ Script pre-generated during music');
    
    setTotalNewsCount(news.length);
    setIsLoadingCards(false);
    setIsFetchingNewCycle(false);
    setIsSearchingComplete(true);  // ✅ NOW show transcript/content

    log('🔴 NUCLEAR: Ensuring 100% intermission stop...');
    await nuclearStopAllAudio();
    
    let stopAttempts = 0;
    while ((currentAudioRef.current !== null || intermissionAudioRef.current !== null) && stopAttempts < 5) {
      await nuclearStopAllAudio();
      await new Promise(resolve => setTimeout(resolve, 100));
      stopAttempts++;
    }
    
    setTranscript([]);
    setCurrentStatus('Broadcasting...');
    
    log('✅ Starting broadcast...');
    
    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }
    
    log('🎙️ Playing pre-generated script...');
    
    setCurrentCardIndex(0);
    completedCardsRef.current.clear();
    setCompletedCards(new Set());
    
    saveLocalNewsSession(0, 0, completedCardsRef.current);
    
    await playScriptWithCards(script, news);
    
    if (shouldStopRef.current) return;
    
    log('✅ All 5 cards complete, preparing for next cycle...');
    
    setCurrentCycle(prev => prev + 1);
    
    log('🔄 Cycle complete, looping back to search for more news...');
    
  } catch (error) {
    log(`❌ News cycle error: ${error.message}`);
    await new Promise(resolve => setTimeout(resolve, 10000));
    return runNewsCycle();
  }
};
const runContinuousStation = async () => {
  log('=== STARTING LOCAL NEWS STATION ===');
  // ✅ Mark that user has used local news (for notifications)
await markUserUsedLocalNews();
  shouldStopRef.current = false;
  setIsPlaying(true);
  setIsPaused(false);
  setTranscript([]);
  
  try {
    const existingSession = await getLocalNewsSession();
    let resumingFromSession = false;
    let startCardIndex = 0;
    let startLineIndex = 0;
    
    if (existingSession && existingSession.newsCards && existingSession.newsCards.length > 0) {
      const sessionDate = new Date(existingSession.timestamp);
      const today = new Date();
      const isSameDay = sessionDate.toDateString() === today.toDateString();
      
      if (isSameDay) {
        log('🔄 Same day return - resuming from saved position');
        resumingFromSession = true;
        
        if (existingSession.heardNewsIds) {
          existingSession.heardNewsIds.forEach(id => heardNewsIdsRef.current.add(id));
          setHeardNewsIds(new Set(heardNewsIdsRef.current));
        }
        
        if (existingSession.currentDaysBack !== undefined) {
          currentDaysBackRef.current = existingSession.currentDaysBack;
          setCurrentDaysBack(existingSession.currentDaysBack);
        }
        
        const restoredCards = existingSession.newsCards.map((card, idx) => ({
          ...card,
          isReady: idx <= existingSession.lastCardIndex,
          isActive: idx === existingSession.lastCardIndex
        }));
        
        setNewsCards(restoredCards);
        setCurrentCardIndex(existingSession.lastCardIndex);
        startCardIndex = existingSession.lastCardIndex;
        startLineIndex = existingSession.lastLineIndex || 0;
        
        if (existingSession.completedCards) {
          existingSession.completedCards.forEach(cardId => completedCardsRef.current.add(cardId));
          setCompletedCards(new Set(completedCardsRef.current));
        }
        
        if (existingSession.hasGuest && existingSession.guestName) {
          setHasGuest(existingSession.hasGuest);
          setGuestName(existingSession.guestName);
        }
        
        if (existingSession.currentCycle !== undefined) {
          setCurrentCycle(existingSession.currentCycle);
        }
        
        setIsLoadingCards(false);
        setIsSearchingComplete(true);
        
        setTimeout(() => {
          if (newsCardsRef.current?.children[startCardIndex]) {
            newsCardsRef.current.children[startCardIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }, 300);
      }
    }
    
    // ✅ ALWAYS play intro music AND show loading message
    log('🎵 Playing intro music...');
    
    const greetingText = resumingFromSession 
      ? `Resuming, ${userName}...` 
      : `One sec, ${userName}...`;
    
    setTranscript([{ 
      voice: 'system', 
      text: greetingText, 
      timestamp: Date.now() 
    }]);
    
    setIsLoadingCards(false);
    setIsSearchingComplete(resumingFromSession);
    
    // ✅ Play intro music (5 seconds)
    const musicPromise = playIntroMusicWithFade(5);
    
    // ✅ If resuming, generate script during music
    if (resumingFromSession && existingSession.newsCards.length > 0) {
      const newsItems = existingSession.newsCards.map(card => ({
        title: card.fullTitle,
        snippet: card.snippet,
        source: card.source,
        url: card.url,
        time: card.time,
        imageUrl: card.image
      }));
      
      const includeGuest = existingSession.hasGuest || false;
      const guest = includeGuest && existingSession.guestName 
        ? { name: existingSession.guestName, voice: existingSession.guestName === 'Kenny' ? 'guest1' : 'guest2' } 
        : null;
      
      // ✅ Generate script in parallel with intro music
      const scriptPromise = generateStationScript(newsItems, includeGuest, guest?.name || '', true);
      
      // ✅ Wait for BOTH to finish
      const [script] = await Promise.all([scriptPromise, musicPromise]);
      
      if (shouldStopRef.current) {
        setIsPlaying(false);
        return;
      }
      
      log('✅ Script pre-generated during intro music');
      
      setTranscript([]);
      setTotalNewsCount(newsItems.length);
      setCurrentStatus('Broadcasting...');
      
      if (includeGuest && guest) {
        setHasGuest(true);
        setGuestName(guest.name);
      }
      
      const lines = script.split('\n').filter(line => line.trim());
      scriptLinesRef.current = lines;
      
      await resumeFromLine(startLineIndex);
      
      if (shouldStopRef.current) {
        setIsPlaying(false);
        return;
      }
    } else {
      // ✅ NOT resuming - wait for intro music, then ONE unique intro from ONE host
      await musicPromise;
      
      if (shouldStopRef.current) {
        setIsPlaying(false);
        return;
      }
      
      setTranscript([]);
      
      // ✅ GENERATE ONE UNIQUE CREATIVE INTRO - SINGLE HOST
      const timeOfDay = getTimeOfDay();
      
      const introData = await SecureAPI.callAzureOpenAI(
        [
          { 
            role: 'system', 
            content: `You are a local news station host. Create ONE complete intro speech that includes:
1. A unique, creative greeting to the listener
2. A natural transition to "let me get that update for you" or similar

Make it feel fresh, conversational, and genuine. Use "your" and "you" - talk directly TO them. NO "our community" - use "YOUR area" or "YOUR neighborhood". 
Keep total length to 2-3 sentences max. This is ONE continuous speech from ONE host.`
          },
          { 
            role: 'user', 
            content: `Create a complete intro speech for ${userName} about ${userLocation.city}, ${userLocation.state} local news. It's ${timeOfDay}. 

Include:
- A creative, unique greeting (never repeats the same format)
- A natural transition to "let me get that update for you" or similar
- Keep it to 3-4 sentences total

Make it feel like one host doing a complete intro. Address ${userName} directly. Talk about THEIR community/area.

Examples of creative angles:
- Start with something surprising or question
- Reference the time of day
- Paint a quick scene about the area
- Build curiosity
- Reference what's likely happening right now in their area` 
          }
        ],
        {
          temperature: 1.0,
          max_tokens: 150
        }
      );

      const completeIntro = introData.choices[0].message.content.trim();
      
      log(`🎙️ Generated complete intro: ${completeIntro}`);
      
      // ✅ PRELOAD NEXT AUDIO while intro plays
      void preloadAudio(`Here's what's happening in ${userLocation.city} today.`, 'guide');
      
      // ✅ PLAY SINGLE INTRO - ONE HOST ONLY
      await playAudio(completeIntro, 'therapist');
      
      if (shouldStopRef.current) {
        setIsPlaying(false);
        return;
      }
    }
    
    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }
    
    setTranscript([]);
    
    // ✅ CONTINUOUS LOOP - Never stops
    while (!shouldStopRef.current) {
      await runNewsCycle();
      
      if (shouldStopRef.current) break;
      
      await nuclearStopAllAudio();
      
      let attempts = 0;
      while ((currentAudioRef.current !== null || intermissionAudioRef.current !== null) && attempts < 3) {
        await nuclearStopAllAudio();
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }
      
      setTranscript([]);
      
      if (shouldStopRef.current) break;
    }
    
  } catch (error) {
    log(`❌ Station error: ${error.message}`);
    alert('Failed to run station. Please try again.');
    setIsPlaying(false);
  }
};


  const sessionDataRef = useRef({
    lastPausedAt: null,
    lastLineIndex: 0,
    currentNewsIndex: 0,
    currentCycle: 0
  });

  const log = useCallback((message) => {
    console.log(`[Local Station] ${message}`);
  }, []);
  // Add this function after the playScript function
const resumeFromLine = async (startLineIndex) => {
  log(`▶️ Resuming from line ${startLineIndex}`);
  const lines = scriptLinesRef.current;
  
  if (!lines || lines.length === 0) {
    log('⚠️ No script lines available');
    return;
  }
  
  for (let i = startLineIndex; i < lines.length; i++) {
    if (shouldStopRef.current) {
      log('⏹️ Stop requested, breaking resume loop');
      break;
    }
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);

if (guideMatch || companionMatch) {
      const voice = guideMatch ? 'guide' : 'therapist';
      const text = (guideMatch || companionMatch)[1].trim();

      // ✅ ADD THIS: Preload next speaker
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const nextGuideMatch = nextLine.match(/Guide:\s*(.+)/i);
        const nextCompanionMatch = nextLine.match(/Companion:\s*(.+)/i);

        let nextVoice, nextText;
        
        if (nextGuideMatch) {
          nextVoice = 'guide';
          nextText = nextGuideMatch[1].trim();
        } else if (nextCompanionMatch) {
          nextVoice = 'therapist';
          nextText = nextCompanionMatch[1].trim();
        }

        if (nextVoice && nextText) {
          void preloadAudio(nextText, nextVoice); // ✅ Preload next speaker
        }
      }

      await playAudio(text, voice);
      
      if (shouldStopRef.current) break;
    }
  }
  
  log('✅ Resume playback complete');
};

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);
useEffect(() => {
  if (transcriptEndRef.current && transcript.length > 0) {
    // ✅ Scroll to TOP of container (where latest transcript is)
    transcriptEndRef.current.scrollTop = 0;
  }
}, [transcript]);

useEffect(() => {
  if (transcriptEndRef.current) {
    transcriptEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}, [transcript]);

  const getLastUsedMusic = (type) => {
    try {
      const stored = localStorage.getItem(`lastUsed${type}Music`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const setLastUsedMusic = (type, url) => {
    try {
      localStorage.setItem(`lastUsed${type}Music`, JSON.stringify(url));
    } catch {
      log('⚠️ Could not save music preference');
    }
  };

  const selectRandomMusicNeverRepeat = (musicOptions, type) => {
    const lastUsed = getLastUsedMusic(type);
    const available = musicOptions.filter(url => url !== lastUsed);
    const choices = available.length > 0 ? available : musicOptions;
    const selected = choices[Math.floor(Math.random() * choices.length)];
    setLastUsedMusic(type, selected);
    log(`🎵 Selected ${type} music`);
    return selected;
  };

  const trimSilenceAggressive = async (blob) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const threshold = 0.005;
      const sampleRate = audioBuffer.sampleRate;
      const channels = audioBuffer.numberOfChannels;
      
      let start = 0;
      const channel0 = audioBuffer.getChannelData(0);
      
      for (let i = 0; i < channel0.length; i++) {
        if (Math.abs(channel0[i]) > threshold) {
          start = i;
          break;
        }
      }
      
      let end = channel0.length;
      for (let i = channel0.length - 1; i >= start; i--) {
        if (Math.abs(channel0[i]) > threshold) {
          end = i + 1;
          break;
        }
      }
      
      const trimmedLength = end - start;
      if (trimmedLength <= 0) return blob;
      
      const trimmedBuffer = audioContext.createBuffer(channels, trimmedLength, sampleRate);
      
      for (let ch = 0; ch < channels; ch++) {
        const sourceData = audioBuffer.getChannelData(ch);
        const targetData = trimmedBuffer.getChannelData(ch);
        targetData.set(sourceData.slice(start, end));
      }
      
      const trimmedBlob = audioBufferToWavBlob(trimmedBuffer);
      await audioContext.close();
      
      return trimmedBlob;
    } catch (error) {
      log(`⚠️ Trim failed: ${error.message}`);
      return blob;
    }
  };

  const audioBufferToWavBlob = (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length;
    
    const interleaved = new Float32Array(length * numberOfChannels);
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        interleaved[i * numberOfChannels + channel] = audioBuffer.getChannelData(channel)[i];
      }
    }
    
    const pcmData = new Int16Array(interleaved.length);
    for (let i = 0; i < interleaved.length; i++) {
      const sample = Math.max(-1, Math.min(1, interleaved[i]));
      pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    
    const wavBuffer = new ArrayBuffer(44 + pcmData.length * 2);
    const view = new DataView(wavBuffer);
    
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + pcmData.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, pcmData.length * 2, true);
    
    const dataView = new Int16Array(wavBuffer, 44);
    dataView.set(pcmData);
    
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };
  // ============================================
// PRELOAD AUDIO - Cache next speaker
// ============================================

const preloadAudio = async (text, voice) => {
  const cacheKey = `${voice}-${text}`;
  
  // ✅ CHECK IF ALREADY CACHED
  if (audioQueueRef.current.has(cacheKey)) {
    log(`✅ ${voice.toUpperCase()} ALREADY CACHED`);
    return true;
  }

  // ✅ CHECK IF ALREADY PRELOADING
  if (preloadAbortControllers.current.has(cacheKey)) {
    log(`⏳ ${voice.toUpperCase()} PRELOADING, WAITING...`);
    let waitCount = 0;
    const maxWait = 300; // 15 seconds max
    while (preloadAbortControllers.current.has(cacheKey) && waitCount < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 50));
      waitCount++;
    }
    if (audioQueueRef.current.has(cacheKey)) {
      log(`✅ ${voice.toUpperCase()} READY`);
      return true;
    }
    log(`⚠️ ${voice.toUpperCase()} TIMEOUT`);
    return false;
  }

  // ✅ CREATE ABORT CONTROLLER FOR THIS PRELOAD
  const preloadController = new AbortController();
  preloadAbortControllers.current.set(cacheKey, preloadController);

  log(`🔄 PRELOADING ${voice.toUpperCase()}: ${text.substring(0, 40)}...`);

  try {
    // ✅ USE SECURE API - NO EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      1.1
    );

    const url = URL.createObjectURL(audioBlob);

    audioQueueRef.current.set(cacheKey, { url, text, voice, used: false });
    
    log(`✅ ${voice.toUpperCase()} LOADED & READY FOR INSTANT PLAY`);
    return true;

  } catch (error) {
    if (error.name !== 'AbortError') {
      log(`⚠️ Preload error for ${voice}: ${error.message}`);
    }
    return false;
  } finally {
    preloadAbortControllers.current.delete(cacheKey);
  }
};


// ============================================
// REPLACE playAudio FUNCTION
// ============================================

const playAudio = async (text, voice) => {
  if (shouldStopRef.current || isPausedRef.current) {
    log('⏸️ Paused - not starting audio');
    return;
  }
  
  setTranscript(prev => [...prev, { voice, text, timestamp: Date.now() }]);

  // ✅ CHECK CACHE FIRST FOR INSTANT PLAYBACK
  const cacheKey = `${voice}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  if (cached && !cached.used) {
    cached.used = true;
    log(`⚡⚡⚡ INSTANT PLAYBACK: ${voice.toUpperCase()}`);

    return new Promise((resolve) => {
      if (shouldStopRef.current || isPausedRef.current) {
        resolve();
        return;
      }
      
      const audio = new Audio(cached.url);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        log(`✅ ${voice.toUpperCase()} instant playback finished`);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        log(`❌ ${voice.toUpperCase()} instant playback error`);
        resolve();
      };

      log(`▶️ Playing INSTANT ${voice.toUpperCase()} NOW`);
      audio.play().catch(err => {
        log(`⚠️ Play error: ${err.message}`);
        resolve();
      });
    });
  }

  // ❌ NOT CACHED - Generate live (slower)
  log(`⚠️ ${voice.toUpperCase()} NOT IN CACHE - generating fresh`);

  try {
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Stopped before TTS request');
      return;
    }
    
    // ✅ USE SECURE API - NO EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      playbackSpeedRef.current
    );

    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Stopped after TTS generation');
      return;
    }

    const trimmedBlob = await trimSilenceAggressive(audioBlob);
    const audioUrl = URL.createObjectURL(trimmedBlob);

    return new Promise((resolve) => {
      if (shouldStopRef.current || isPausedRef.current) {
        URL.revokeObjectURL(audioUrl);
        log('⏸️ Stopping before audio play');
        resolve();
        return;
      }

      const audio = new Audio(audioUrl);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        log(`✅ ${voice.toUpperCase()} fresh playback finished`);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        log(`❌ ${voice.toUpperCase()} fresh playback error`);
        resolve();
      };

      log(`▶️ Playing FRESH ${voice.toUpperCase()}`);
      audio.play().catch(err => {
        log(`⚠️ Play error: ${err.message}`);
        URL.revokeObjectURL(audioUrl);
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        resolve();
      });
    });
  } catch (error) {
    log(`Audio error: ${error.message}`);
  }
};



const playIntroMusicWithFade = async (duration = 5) => {
  try {
    // ✅ CHECK if paused/stopped BEFORE starting
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Skipping intro music - paused/stopped');
      return;
    }
    
    const musicUrl = selectRandomMusicNeverRepeat(INTRO_MUSIC_OPTIONS, 'Intro');
    const audio = new Audio(musicUrl);
    audio.volume = 0.4;
    audio.crossOrigin = 'anonymous';
    
    // ✅ REGISTER IN GUEST LIST
    activeAudioElements.current.add(audio);
    currentAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        // ✅ CHECK AGAIN before playing
        if (shouldStopRef.current || isPausedRef.current) {
          activeAudioElements.current.delete(audio);
          currentAudioRef.current = null;
          log('⏸️ Cancelled intro music - paused');
          resolve();
          return;
        }
        
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        
        setTimeout(async () => {
          if (!shouldStopRef.current) {
            await fadeOutMusicIntoHosts(audio, 800);
          }
          activeAudioElements.current.delete(audio);
          resolve();
        }, duration * 1000);
      };
      
      audio.onerror = () => {
        log(`⚠️ Intro music failed to load`);
        activeAudioElements.current.delete(audio);
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intro music error: ${error.message}`);
  }
};

const playIntermissionMusicWithFade = async () => {
  try {
    // ✅ CHECK if paused/stopped BEFORE starting
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Skipping intermission music - paused/stopped');
      return;
    }
    
    const musicUrl = selectRandomMusicNeverRepeat(INTERMISSION_MUSIC_OPTIONS, 'Intermission');
    const audio = new Audio(musicUrl);
    audio.volume = 0.3;
    audio.loop = true;
    audio.crossOrigin = 'anonymous';
    
    // ✅ REGISTER IN GUEST LIST
    activeAudioElements.current.add(audio);
    currentAudioRef.current = audio;
    intermissionAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        // ✅ CHECK AGAIN before playing
        if (shouldStopRef.current || isPausedRef.current) {
          activeAudioElements.current.delete(audio);
          currentAudioRef.current = null;
          intermissionAudioRef.current = null;
          log('⏸️ Cancelled intermission music - paused');
          resolve();
          return;
        }
        
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        resolve();
      };
      
      audio.onerror = () => {
        log(`⚠️ Intermission music failed to load`);
        activeAudioElements.current.delete(audio);
        intermissionAudioRef.current = null;
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intermission music error: ${error.message}`);
    intermissionAudioRef.current = null;
  }
};
const nuclearStopAllAudio = async () => {
  log('🔴 NUCLEAR STOP: Killing all audio sources...');
  
  // ✅ Stop current audio ref
  if (currentAudioRef.current) {
    try {
      const audio = currentAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
      audio.removeAttribute('src');
      currentAudioRef.current = null;
      log('✅ Current audio killed');
    } catch (e) {
      log(`⚠️ Error stopping current audio: ${e.message}`);
      currentAudioRef.current = null;
    }
  }
  
  // ✅ Stop intermission audio ref
  if (intermissionAudioRef.current) {
    try {
      const audio = intermissionAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
      audio.removeAttribute('src');
      intermissionAudioRef.current = null;
      log('✅ Intermission audio killed');
    } catch (e) {
      log(`⚠️ Error stopping intermission: ${e.message}`);
      intermissionAudioRef.current = null;
    }
  }
  
  // ✅ Wait for browser to release resources
  await new Promise(resolve => setTimeout(resolve, 300));
  
  log('✅ All audio sources terminated');
};
const fadeOutMusicIntoHosts = async (audioElement, fadeOutDuration = 600) => {
  if (!audioElement) return;
  
  return new Promise((resolve) => {
    const startVolume = audioElement.volume;
    const steps = 50;
    const volumeStep = startVolume / steps;
    const timeStep = fadeOutDuration / steps;
    let currentStep = 0;
    
    const fadeInterval = setInterval(() => {
      if (currentStep >= steps || shouldStopRef.current) {
        audioElement.pause();
        audioElement.volume = 0;
        audioElement.currentTime = 0;
        activeAudioElements.current.delete(audioElement); // ✅ Remove from guest list
        currentAudioRef.current = null;
        clearInterval(fadeInterval);
        resolve();
        return;
      }
      
      if (audioElement) {
        audioElement.volume = Math.max(0, startVolume - (volumeStep * currentStep));
      }
      currentStep++;
    }, timeStep);
  });
};


  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  };

const fetchNews = async (daysBack = 0) => {
  try {
    log('\n╔════════════════════════════════════════════╗');
    log(`║  🌍 FETCHING LOCAL NEWS (Days back: ${daysBack})    ║`);
    log('╚════════════════════════════════════════════╝');
    log(`⏰ Searching for news from ${daysBack} days back`);
    log(`📦 Currently heard: ${heardNewsIdsRef.current.size} articles`);
    
    // ✅ Fetch news data
    const newsData = await fetchLocalNewsHandler(true, daysBack);
    
    if (!newsData || !newsData.mainCity) {
      throw new Error('No news data available');
    }
    
    const allArticles = [];
    const maxHoursAgo = daysBack * 24;
    
    log(`\n✅ News data received:`);
    log(`   Main City: ${newsData.mainCity.name}`);
    log(`   Nearby Cities: ${newsData.nearbyCities?.length || 0}`);
    log(`   State: ${newsData.stateNews?.name || 'None'}`);
    
    // ✅ Helper function to add articles with time filtering
    const addArticlesFromSource = (articles, sourceName) => {
      if (!articles || articles.length === 0) return 0;
      
      let added = 0;
      let filtered = 0;
      let outOfRange = 0;
      
      for (const article of articles) {
        const articleId = `${article.url}-${article.title}`;
        
        // Skip if already heard
        if (heardNewsIdsRef.current.has(articleId)) {
          filtered++;
          continue;
        }
        
        // Check time range
        if (article.hoursAgo < (daysBack === 0 ? 0 : (daysBack - 1) * 24) || 
            article.hoursAgo > maxHoursAgo + 24) {
          outOfRange++;
          continue;
        }
        
        allArticles.push({
          id: articleId,
          title: article.title,
          snippet: article.snippet,
          source: sourceName,
          url: article.url,
          time: article.timeAgo,
          imageUrl: article.imageUrl,
          hoursAgo: article.hoursAgo || 0
        });
        
        added++;
        
        // ✅ EARLY EXIT: Stop when we hit 5
        if (allArticles.length >= 5) {
          log(`   ⚡ Hit 5 articles - stopping!`);
          return added;
        }
      }
      
      if (filtered > 0) {
        log(`   ⏭️ Filtered: ${filtered} (already heard)`);
      }
      if (outOfRange > 0) {
        log(`   ⏭️ Out of range: ${outOfRange} (wrong time period)`);
      }
      
      return added;
    };
    
    // ============================================
    // Priority 1: MAIN CITY
    // ============================================
    log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
    log(`┃  PRIORITY 1: ${newsData.mainCity.name.padEnd(27)}┃`);
    log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
    
    if (newsData.mainCity.articles) {
      log(`📰 Raw articles from handler: ${newsData.mainCity.articles.length}`);
      
      const added = addArticlesFromSource(newsData.mainCity.articles, newsData.mainCity.name);
      
      log(`✅ Added to pool: ${added}`);
      log(`📊 Current pool size: ${allArticles.length}/5`);
      
      if (allArticles.length >= 5) {
        log(`\n✅ SUCCESS: Found 5 articles from ${newsData.mainCity.name}!`);
        log(`🛑 Stopping search - no need to check other sources`);
        
        const selected = allArticles.slice(0, 5);
        
        // Mark as heard
        selected.forEach(article => heardNewsIdsRef.current.add(article.id));
        setHeardNewsIds(new Set(heardNewsIdsRef.current));
        
        log(`\n╔════════════════════════════════════════════╗`);
        log(`║  📋 FINAL SELECTION (${selected.length} articles)          ║`);
        log(`╚════════════════════════════════════════════╝`);
        
        selected.forEach((article, idx) => {
          log(`${idx + 1}. [${article.source}]`);
          log(`   "${article.title.substring(0, 60)}..."`);
          log(`   ${article.time}`);
        });
        
        log(`\n✅ Marked ${selected.length} articles as heard`);
        log(`📊 Total heard articles: ${heardNewsIdsRef.current.size}`);
        
        // Convert to cards
        const cards = selected.map((article, idx) => ({
          id: idx,
          articleId: article.id,
          title: article.title.split(' ').slice(0, 4).join(' '),
          fullTitle: article.title,
          image: article.imageUrl || '/image/studycard10.png',
          source: article.source,
          url: article.url,
          time: article.time,
          isReady: false,
          isActive: false,
          snippet: article.snippet
        }));
        
        setNewsCards(cards);
        
        return selected;
      }
    } else {
      log(`⚠️ No articles from ${newsData.mainCity.name}`);
    }
    
    log(`\n📊 After main city: ${allArticles.length}/5 articles`);
    
    // ============================================
    // Priority 2: NEARBY CITIES
    // ============================================
    if (newsData.nearbyCities && allArticles.length < 5) {
      log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
      log('┃  PRIORITY 2: NEARBY CITIES             ┃');
      log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
      
      for (const city of newsData.nearbyCities) {
        if (allArticles.length >= 5) {
          log(`\n✅ Hit 5 articles - skipping remaining cities`);
          break;
        }
        
        log(`\n🏙️ Checking ${city.name}:`);
        
        if (city.articles && city.articles.length > 0) {
          log(`   📰 Raw articles: ${city.articles.length}`);
          
          const added = addArticlesFromSource(city.articles, city.name);
          
          log(`   ✅ Added to pool: ${added}`);
          log(`   📊 Current pool size: ${allArticles.length}/5`);
          
          if (allArticles.length >= 5) {
            log(`\n✅ SUCCESS: Hit 5 articles with ${city.name}!`);
            log(`🛑 Stopping search`);
            break;
          }
        } else {
          log(`   ⚠️ No articles from ${city.name}`);
        }
      }
    } else if (!newsData.nearbyCities) {
      log('\n⚠️ No nearby cities data available');
    }
    
    log(`\n📊 After nearby cities: ${allArticles.length}/5 articles`);
    
    // ============================================
    // Priority 3: STATE NEWS
    // ============================================
    if (newsData.stateNews?.articles && allArticles.length < 5) {
      log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
      log('┃  PRIORITY 3: STATE NEWS                ┃');
      log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
      
      log(`🏛️ Checking ${newsData.stateNews.name}:`);
      log(`   📰 Raw articles: ${newsData.stateNews.articles.length}`);
      
      const added = addArticlesFromSource(newsData.stateNews.articles, newsData.stateNews.name);
      
      log(`   ✅ Added to pool: ${added}`);
      log(`   📊 Final pool size: ${allArticles.length}/5`);
    }
    
    // ============================================
    // FINAL PROCESSING
    // ============================================
    log('\n╔════════════════════════════════════════════╗');
    log('║  🎯 FINAL PROCESSING                       ║');
    log('╚════════════════════════════════════════════╝');
    
    if (allArticles.length === 0) {
      log('❌ CRITICAL: No articles found in any source!');
      log(`⚠️ Days back: ${daysBack}, Heard: ${heardNewsIdsRef.current.size}`);
      return [];
    }
    
    // Shuffle and take exactly 5
    const shuffled = allArticles.sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 5);
    
    log(`\n✅ Selected ${selected.length} articles`);
    
    // Mark as heard
    selected.forEach(article => {
      heardNewsIdsRef.current.add(article.id);
    });
    setHeardNewsIds(new Set(heardNewsIdsRef.current));
    
    log(`\n╔════════════════════════════════════════════╗`);
    log(`║  📋 FINAL SELECTION (${selected.length} articles)          ║`);
    log(`╚════════════════════════════════════════════╝`);
    
    // Count by source
    const sourceCount = {};
    selected.forEach(article => {
      sourceCount[article.source] = (sourceCount[article.source] || 0) + 1;
    });
    
    log(`\n📊 Source breakdown:`);
    Object.entries(sourceCount).forEach(([source, count]) => {
      log(`   ${source}: ${count} article${count > 1 ? 's' : ''}`);
    });
    
    log(`\n📰 Articles:`);
    selected.forEach((article, idx) => {
      log(`${idx + 1}. [${article.source}]`);
      log(`   "${article.title.substring(0, 60)}..."`);
      log(`   ${article.time}`);
    });
    
    log(`\n✅ Marked ${selected.length} articles as heard`);
    log(`📊 Total heard articles: ${heardNewsIdsRef.current.size}`);
    
    // Convert to cards
    const cards = selected.map((article, idx) => ({
      id: idx,
      articleId: article.id,
      title: article.title.split(' ').slice(0, 4).join(' '),
      fullTitle: article.title,
      image: article.imageUrl || '/image/studycard10.png',
      source: article.source,
      url: article.url,
      time: article.time,
      isReady: false,
      isActive: false,
      snippet: article.snippet
    }));
    
    setNewsCards(cards);
    
    return selected;
    
  } catch (error) {
    log(`❌ News fetch error: ${error.message}`);
    throw error;
  }
};

// Add after the log function
const selectRandomGuest = () => {
  const guests = [
    { name: 'Kenny', voice: 'guest1', intro: 'tech enthusiast' },
    { name: 'Jackie', voice: 'guest2', intro: 'community member' }
  ];
  return guests[Math.floor(Math.random() * guests.length)];
};

const shouldIncludeGuest = (cycleNumber) => {
  // 60% chance of guest appearing
  // Never in first cycle (cycle 0)
  if (cycleNumber === 0) return false;
  return Math.random() < 0.6;
};
useEffect(() => {
  return () => {
    log('🧹 Component unmounting - nuclear cleanup');
    shouldStopRef.current = true;
    isPausedRef.current = true;
    
    // Abort all preloads (if exists)
    if (preloadAbortControllers.current) {
      preloadAbortControllers.current.forEach(controller => {
        try {
          controller.abort();
        } catch (e) {}
      });
      preloadAbortControllers.current.clear();
    }
    
    // Stop all tracked audio
    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
      } catch (e) {}
    });
    activeAudioElements.current.clear();
    
    // Clean up refs
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current = null;
    }
    
    // Clean up cache (if exists)
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      audioQueueRef.current.clear();
    }
  };
}, []);
const generateStationScript = async (newsItems, includeGuest = false, guestName = '', isResuming = false) => {
  const timeOfDay = getTimeOfDay();
  const newsContext = newsItems.map((item, idx) => 
    `${idx + 1}. ${item.title}\nSource: ${item.source}\nSummary: ${item.snippet}`
  ).join('\n\n');

  const guestInfo = includeGuest ? `

**GUEST FEATURE:** ${guestName} (interesting local guest)
- NOT a journalist or expert - just an engaged community member
- Joins naturally around exchange 8-12 (not at start)
- Brings fresh perspective, curiosity, or personal interest in the topic
- Can ask questions, react, share observations
- Makes it feel like 3 friends discussing local news

**GUEST ENTRANCE STYLES (pick one randomly):**

**STYLE 1: Casual Drop-In (most common)**
Guide: "This story is getting interesting."
Companion: "Yeah, I wonder how this affects downtown."
${guestName}: "Wait, are you talking about the Main Street thing?"
Guide: "Yeah, what do you think about it?"

**STYLE 2: Overhearing**
${guestName}: "Sorry, couldn't help overhearing—did you say ${userLocation.city}?"
Companion: "Yeah! ${guestName}, you know about this?"
${guestName}: "A bit. What's the story?"

**STYLE 3: Natural Segue**
Guide: "You know who might have a take on this? ${guestName}."
${guestName}: "Did someone mention my name? What's going on?"
Companion: "We're discussing [topic]. Thoughts?"

**GUEST PARTICIPATION:**
- ${guestName} contributes 25-35% after entering
- Can say things like:
  * "That's wild, I didn't know that"
  * "Hold on, how does that work?"
  * "I've been wondering about this"
  * "Oh interesting"
  * "Wait, really? That changes things"
  * "Hmm, I wonder if..."
- NOT an expert—just engaged and curious
- Can disagree or ask questions
- Feels like a friend joining the conversation
- ❌ CANNOT reference personal experiences like "I was there" or "I saw this"

**GUEST DEPARTURE (natural, not formal):**
Guide or Companion: "Alright, thanks for chiming in ${guestName}."
${guestName}: "Anytime! This was interesting."
(Then continue with just Guide and Companion)

` : '';

  const prompt = `Create a live local news broadcast for ${userLocation.city}, ${userLocation.state}.

**LISTENER:** ${userName}
**TIME OF DAY:** ${timeOfDay}
**SESSION TYPE:** ${isResuming ? 'RESUMING (user came back)' : 'FRESH START'}
${guestInfo}

**LATEST LOCAL NEWS:**
${newsContext}

**CRITICAL: THIS IS A 24/7 CONTINUOUS STATION**
- DO NOT say "that's all for today" or "tune in tomorrow"
- DO NOT wrap up or conclude
- This is ONE SEGMENT of an endless broadcast
- After covering news, say "Let's switch gears while we gather more current updates"
- Keep it open-ended and continuous

**YOU ARE SPEAKING TO ${userName} DIRECTLY**
- Address them naturally in the greeting
- Use their name SPARINGLY after intro - maximum 1-2 times in the rest of the broadcast
- Natural conversation between hosts${includeGuest ? ' and guest' : ''}

**HOSTS:**
- Guide: Main host
- Companion: Co-host
${includeGuest ? `- ${guestName}: Local guest (engaged listener)` : ''}

**INTRO STYLE - NATURAL & COMPLETE:**
${isResuming ? `
This is a RESUMING session - ${userName} came back to continue listening.

✅ GOOD RESUMING INTROS (pick one style randomly):
Guide: "Hey ${userName}, welcome back. Let's dive right back into what's happening in ${userLocation.city}."
Companion: "Yeah, picking up where we left off."

Guide: "Alright ${userName}, good to have you back. Let's keep going with ${userLocation.city} news."
Companion: "Right, so what's next?"

Guide: "${userName}, welcome back. Let's continue with what's happening locally."
Companion: "Sounds good. What do we have?"

Companion: "Hey ${userName}, welcome back. Ready to catch up on ${userLocation.city}?"
Guide: "Let's jump right in."

❌ DON'T SAY:
- Just "${userName}" or "Hey ${userName}" with nothing after (too abrupt)
- "Welcome back to the InnerLoop" (too formal)
- Long explanations about what they missed
` : `
This is a FRESH START session.

✅ GOOD FRESH INTROS (pick one style randomly, vary constantly):

**Style 1: Natural Welcome (50%)**
Guide: "Hey ${userName}, good ${timeOfDay === 'morning' ? 'morning' : timeOfDay === 'afternoon' ? 'afternoon' : 'evening'}. Let's dive into what's happening in ${userLocation.city}."
Companion: "Yeah, we've got some interesting updates today."

Guide: "Good ${timeOfDay === 'morning' ? 'morning' : timeOfDay === 'afternoon' ? 'afternoon' : 'evening'} ${userName}. Let's get into your local news."
Companion: "Alright, what's first?"

Companion: "Hey ${userName}, ${timeOfDay === 'morning' ? 'morning' : timeOfDay === 'afternoon' ? 'afternoon' : 'evening'}. Ready for ${userLocation.city} news?"
Guide: "Let's dive in."

**Style 2: Casual & Direct (30%)**
Guide: "Alright ${userName}, let's see what's happening in ${userLocation.city} today."
Companion: "Got a few things to talk about."

Guide: "Hey ${userName}. Let's get into ${userLocation.city} news."
Companion: "Sounds good. What's up first?"

**Style 3: Time-Based (20%)**
Guide: "${timeOfDay === 'morning' ? 'Morning' : timeOfDay === 'afternoon' ? 'Afternoon' : 'Evening'} ${userName}. Let's talk ${userLocation.city}."
Companion: "Yeah, let's get into it."

Companion: "Hey ${userName}, ${timeOfDay === 'morning' ? 'morning update' : timeOfDay === 'afternoon' ? 'afternoon check-in' : 'evening round-up'} for ${userLocation.city}."
Guide: "Alright, what do we have?"

❌ FORBIDDEN INTROS (never use these):
- Just "Hey ${userName}." or "Morning ${userName}." with nothing after (too abrupt/robotic)
- "Good morning and welcome to your daily local news update" (too formal/scripted)
- "Welcome to the InnerLoop" (no show branding)
- Any greeting shorter than a complete sentence
`}

**CONVERSATION STYLE - NATURAL NEWS DELIVERY:**

Think NPR or BBC - professional but conversational. The goal is to sound like two real people discussing the news naturally, NOT reading a script.

**CRITICAL: SPEAKING TO ${userName} DIRECTLY**
✅ Say "you" when relevant:
- "This could affect your commute"
- "If you're heading downtown..."
- "You'll want to avoid..."
- "This is important for you to know"

❌ NEVER say:
- "for everyone"
- "for all of us"
- "for people"
- "this affects everyone"

Instead say:
- "for you"
- "this affects you"
- "if you're in the area"

**CRITICAL: NO FALSE PERSONAL EXPERIENCES**
❌ NEVER have ANY speaker (Guide, Companion, OR ${guestName || 'Guest'}) say:
- "I was there yesterday"
- "I saw this"
- "I drove by"
- "I talked to someone"
- "We'll see you at..."
- "I heard about this"
- "My friend told me"
- Any reference to personal physical experiences or encounters

✅ ALL SPEAKERS CAN say:
- "That's interesting"
- "Oh wow"
- "This could affect [area]"
- "That makes sense"
- "Hmm, I wonder how..."
- "This is important for people in [area]"
- "That's good to know"
- Natural reactions and observations based ONLY on the news being discussed

**FILLER IS GOOD (when it's natural and brief):**
✅ Natural reactions: "Oh wow", "Hmm", "Yeah", "Right", "Interesting", "Wait, really?"
✅ Brief observations that add context: "That makes sense because traffic is always heavy there"
✅ Quick relatable comments: "That's gonna be a headache for commuters"
✅ Thoughtful questions: "I wonder how long this will last?"

**BUT - Keep Moving Forward:**
- Filler should be BRIEF (1-2 sentences max before getting back to facts)
- After a natural reaction, immediately add context or move to the next point
- Maximum 2-3 exchanges per story before transitioning to next news
- Don't circle back to the same point multiple times
- Each exchange should either inform OR transition

✅ GOOD (natural with purpose):
Guide: "Route twenty-three is backed up—eight-minute delay near Exit fifty-three."
Companion: "Oh man, that's rough for morning commuters. What's the cause?"
Guide: "Accident in the left lane. And heads up, this also affects the Vernon area."

✅ GOOD (brief filler then move on):
Guide: "The city council approved the new parking policy."
Companion: "Interesting. What does this mean for downtown?"
Guide: "Parking rates increase by two dollars starting next month."
Companion: "Alright. Next up, let's talk about the weather."

❌ BAD (too much off-topic discussion):
Guide: "There's a new coffee shop opening."
Companion: "Oh I love coffee shops."
Guide: "Me too, coffee is great."
Companion: "I drink it every morning."
Guide: "Same here, can't function without it."
[This is TOO MUCH - get to the point and move on]

❌ BAD (circling the same point):
Guide: "Traffic is bad on Route twenty-three."
Companion: "Yeah, Route twenty-three is really backed up."
Guide: "Right, so avoid Route twenty-three."
Companion: "Definitely stay away from Route twenty-three."
[Said the same thing 4 times - say it once clearly, then move on]

**CONVERSATION RULES:**
- Spell out ALL numbers as words
- Use natural transitions: "Yeah", "Right", "True", "Hold on", "Wait", "Oh", "Hmm", "Alright"
- Hosts can:
  * Build on each other's points: "And also..." "Plus..." "On top of that..."
  * Agree with NEW information: "Right, and..." "Exactly, plus..." "True, also..."
  * Offer different angles: "Although..." "But..." "On the other hand..."
  * Ask clarifying questions: "Wait, when does this start?" "How much?"
  * React naturally: "Oh wow" "That's significant" "Interesting"
- Each turn: 15-45 words (vary for natural rhythm)
- Keep momentum - brief filler is fine, but always return to facts quickly
- Sound like two informed people briefing ${userName} in a conversational way

**PACING:**
- Spend 2-3 exchanges per simple story (traffic, weather)
- Spend 3-5 exchanges per standard local news story
- Spend 4-6 exchanges per complex story (policy changes, major events)
- Use natural "Mm-hmm", "Yeah", "Right" reactions BUT keep them brief
- After any filler or reaction, immediately add new information or transition
- Don't spend more than 6 exchanges on any single story

${includeGuest ? `**GUEST PARTICIPATION:**
- ${guestName} joins around exchange 8-12 naturally
- Reacts authentically: "Oh wow", "Wait really?", "Interesting", "Hmm"
- Can ask questions: "Does this affect downtown?" "How long will this last?"
- Adds observations: "That makes sense" "This is significant for the area"
- ❌ CANNOT reference personal experiences or physical presence anywhere
- Participates 25-30% after entering
- Exits naturally with brief thanks` : ''}

**SCRIPT STRUCTURE:**
**NEWS COVERAGE** (14-20 exchanges total)
   - Start with complete, natural intro (see styles above)
   - Cover each news item with natural back-and-forth
   - Brief filler/reactions are fine, but always return to facts
   - Hosts work together to inform ${userName}
   - Reference specific details from sources
   - Explain implications naturally
   - Transition smoothly between stories
   ${includeGuest ? `- ${guestName} joins mid-cycle, contributes, then exits` : ''}

${includeGuest ? `**GUEST DEPARTURE** (1 exchange)
   - Host: "Thanks ${guestName}." or "Good talking with you."
   - ${guestName}: "Anytime!" or "Sure thing."` : ''}

**TRANSITION** (1-2 exchanges)
   - "Let's switch gears while we gather more updates."
   - Keep it continuous

Output ONLY the script:
Guide: [text]
Companion: [text]
${includeGuest ? `${guestName}: [text]` : ''}

CRITICAL REMINDERS:
- Intro must be a COMPLETE natural greeting, not just "Hey ${userName}"
- Natural filler and reactions are GOOD and make it human
- But don't circle the same point or go off-topic for too long
- NO speaker can reference personal physical experiences
- Sound professional but genuinely conversational
- This is natural news delivery, not a robotic script OR a rambling podcast`;

try {
  const data = await SecureAPI.callAzureOpenAI(
    [
      { 
        role: 'system', 
        content: `You are a professional broadcast script writer for a 24/7 local news station. Create natural, conversational scripts that sound like real people talking about the news. Brief filler and natural reactions are good - they make it human. But keep focus on delivering information efficiently. Never have any speaker reference personal physical experiences they couldn't have.`
      },
      { role: 'user', content: prompt }
    ],
    {
      temperature: 0.78,
      max_tokens: 1500
    }
  );

  return data.choices[0].message.content;
  } catch (error) {
    log(`❌ Script generation error: ${error.message}`);
    throw error;
  }
};
  const playScript = async (script, hasGuest, guestName) => {
    const lines = script.split('\n').filter(line => line.trim());
    scriptLinesRef.current = lines;
    
    log(`📝 Script has ${lines.length} lines`);
    
    for (let i = 0; i < lines.length; i++) {
      if (shouldStopRef.current) break;
      
      while (isPausedRef.current && !shouldStopRef.current) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      if (shouldStopRef.current) break;
      
      const line = lines[i];
      const guideMatch = line.match(/Guide:\s*(.+)/i);
      const companionMatch = line.match(/Companion:\s*(.+)/i);
      const guestMatch = hasGuest ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i')) : null;

if (guideMatch || companionMatch || guestMatch) {
  let voice, text;
  
  if (guideMatch) {
    voice = 'guide';
    text = guideMatch[1].trim();
  } else if (companionMatch) {
    voice = 'therapist';
    text = companionMatch[1].trim();
  } else if (guestMatch) {
    // Use the guest's assigned voice from the cycle
    const currentGuest = selectRandomGuest();
    voice = currentGuest.voice;
    text = guestMatch[1].trim();
  }

        await playAudio(text, voice);
        
        if (shouldStopRef.current) break;
      }
    }
    
    log('✅ Script playback complete');
  };
const playScriptWithCards = async (script, newsItems) => {
  const lines = script.split('\n').filter(line => line.trim());
  scriptLinesRef.current = lines;
  
  log(`📝 Script has ${lines.length} lines for ${newsItems.length} stories`);
  
  const currentGuestName = guestName;
  const currentHasGuest = hasGuest;
  
  if (currentHasGuest) {
    log(`🎙️ Guest in this segment: ${currentGuestName}`);
  }
  
  const linesPerNews = Math.floor(lines.length / newsItems.length);
  let lastNewsIndex = -1;
  
  for (let i = 0; i < lines.length; i++) {
    if (shouldStopRef.current) {
      log('⏹️ Playback stopped');
      break;
    }
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    const newsIndex = Math.min(Math.floor(i / linesPerNews), newsItems.length - 1);
    
    if (newsIndex !== lastNewsIndex) {
      // ✅ Mark previous card as completed when moving to next
      if (lastNewsIndex >= 0 && newsCards[lastNewsIndex]) {
        completedCardsRef.current.add(newsCards[lastNewsIndex].id);
        setCompletedCards(new Set(completedCardsRef.current));
        log(`✅ Card ${lastNewsIndex + 1} marked as completed`);
      }
      
      log(`📰 Now discussing story ${newsIndex + 1}/${newsItems.length}: "${newsItems[newsIndex].title}"`);
      lastNewsIndex = newsIndex;
      setCurrentCardIndex(newsIndex);
      
      setNewsCards(prev => prev.map((card, idx) => ({
        ...card,
        isActive: idx === newsIndex,
        isReady: idx <= newsIndex
      })));
      
      await saveLocalNewsSession(newsIndex, i, completedCardsRef.current);
      
      setTimeout(() => {
        if (newsCardsRef.current) {
          const cardElement = newsCardsRef.current.children[isLoadingCards ? 0 : newsIndex];
          if (cardElement) {
            cardElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest', 
              inline: 'center' 
            });
          }
        }
      }, 100);
    }
    
    const line = lines[i];
    
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = currentHasGuest && currentGuestName
      ? line.match(new RegExp(`${currentGuestName}:\\s*(.+)`, 'i'))
      : null;

    // ✅ ONLY process if this line has actual dialogue
    if (guideMatch || companionMatch || guestMatch) {
      let voice, text, speakerName;
      
      if (guideMatch) {
        voice = 'guide';
        text = guideMatch[1].trim();
        speakerName = 'Guide';
      } else if (companionMatch) {
        voice = 'therapist';
        text = companionMatch[1].trim();
        speakerName = 'Companion';
      } else if (guestMatch) {
        if (currentGuestName === 'Kenny') {
          voice = 'guest1';
        } else if (currentGuestName === 'Jackie') {
          voice = 'guest2';
        } else {
          voice = 'guest1';
        }
        text = guestMatch[1].trim();
        speakerName = currentGuestName;
        log(`🎤 ${speakerName} speaking: "${text.substring(0, 50)}..."`);
      }

      // ✅ PRELOAD NEXT SPEAKER (but don't play it!)
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const nextGuideMatch = nextLine.match(/Guide:\s*(.+)/i);
        const nextCompanionMatch = nextLine.match(/Companion:\s*(.+)/i);
        const nextGuestMatch = currentHasGuest && currentGuestName
          ? nextLine.match(new RegExp(`${currentGuestName}:\\s*(.+)`, 'i'))
          : null;

        let nextVoice, nextText;
        
        if (nextGuideMatch) {
          nextVoice = 'guide';
          nextText = nextGuideMatch[1].trim();
        } else if (nextCompanionMatch) {
          nextVoice = 'therapist';
          nextText = nextCompanionMatch[1].trim();
        } else if (nextGuestMatch) {
          nextVoice = currentGuestName === 'Kenny' ? 'guest1' : 'guest2';
          nextText = nextGuestMatch[1].trim();
        }

        if (nextVoice && nextText) {
          void preloadAudio(nextText, nextVoice); // ✅ Only preload, don't play
        }
      }

      // ✅ PLAY CURRENT AUDIO - this handles cache or generation
      await playAudio(text, voice);
      
      await saveLocalNewsSession(newsIndex, i, completedCardsRef.current);
      
      if (shouldStopRef.current) break;
    }
    // ✅ If line doesn't match any speaker, skip it (don't try to play empty lines)
  }
  
  // ✅ Mark the LAST card as completed when script finishes
  if (lastNewsIndex >= 0 && newsCards[lastNewsIndex]) {
    completedCardsRef.current.add(newsCards[lastNewsIndex].id);
    setCompletedCards(new Set(completedCardsRef.current));
    log(`✅ Final card ${lastNewsIndex + 1} marked as completed`);
  }
  
  const finalCardIndex = newsCards.length - 1;
  const finalLineIndex = lines.length - 1;
  await saveLocalNewsSession(finalCardIndex, finalLineIndex, completedCardsRef.current);
  
  log(`✅ Script segment complete - ${completedCardsRef.current.size}/5 cards completed`);
};


const togglePlayPause = async () => {
  if (isPaused) {
    // RESUME
    log('▶️ Resuming playback...');
    
    setIsPaused(false);
    isPausedRef.current = false;
    shouldStopRef.current = false;
    
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (scriptLinesRef.current && scriptLinesRef.current.length > 0) {
      const currentLineIndex = transcript.length > 0 
        ? Math.min(transcript.length, scriptLinesRef.current.length - 1)
        : 0;
      
      log(`▶️ Resuming from line ${currentLineIndex}`);
      await resumeFromLine(currentLineIndex);
    }
    
  } else if (isPlaying) {
    // PAUSE - INSTANT STOP
    log('⏸️ INSTANT PAUSE - Complete audio shutdown');
    
    // ✅ Set pause flags FIRST
    setIsPaused(true);
    isPausedRef.current = true;
    shouldStopRef.current = true;
    
    // ✅ NUCLEAR CLEANUP (kills everything including music)
    await nuclearCleanupAudio();
    
    // ✅ Wait for full stop
    await new Promise(resolve => setTimeout(resolve, 300));
    
    log('⏸️ Pause complete');
    
    // ✅ Reset shouldStop flag (so resume works)
    shouldStopRef.current = false;
    
  } else {
    // START
    log('▶️ Starting station...');
    shouldStopRef.current = false;
    isPausedRef.current = false;
    setIsPaused(false);
    
    runContinuousStation(); // or runLiveStation() for CompaniesLiveStation
  }
};
const handleClose = async () => {
  log('🔴 Closing station - COMPLETE SHUTDOWN');
  
  // ✅ CRITICAL: Stop everything immediately
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  // ✅ NUCLEAR CLEANUP (kills all audio including music)
  await nuclearCleanupAudio();
  
  // ✅ Wait for full cleanup
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // ✅ Reset all state
  setIsPlaying(false);
  setIsPaused(false);
  setTranscript([]);
  setNewsCards([]);
  setCurrentCardIndex(0);
  
  log('✅ Station closed - all audio stopped');
  
  onClose();
};


// ✅ FIRESTORE SESSION MANAGEMENT (replace all localStorage functions)

const saveLocalNewsSessionToFirestore = async (cardIndex, scriptLineIndex, completedCardIds) => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready, skipping session save');
      return;
    }

    const sessionData = {
      lastCardIndex: cardIndex,
      lastLineIndex: scriptLineIndex,
      completedCards: Array.from(completedCardIds),
      hasGuest: hasGuest,
      guestName: guestName,
      currentCycle: currentCycle,
      heardNewsIds: Array.from(heardNewsIdsRef.current),
      currentDaysBack: currentDaysBackRef.current,
      newsCards: newsCards.map(card => ({
        id: card.id,
        articleId: card.articleId,
        title: card.title,
        fullTitle: card.fullTitle,
        image: card.image,
        source: card.source,
        url: card.url,
        time: card.time,
        snippet: card.snippet
      })),
      timestamp: Date.now(),
      date: new Date().toDateString(),
      location: userLocation
    };

    const userRef = window.firestoreService.db.collection('users')
      .doc(window.firestoreService.currentUser.uid)
      .collection('localNewsStations')
      .doc('currentSession');

    await userRef.set(sessionData);
    
    log(`✅ Session saved to Firestore: card ${cardIndex}/5, heard: ${heardNewsIdsRef.current.size} articles, searching ${currentDaysBackRef.current} days back`);
  } catch (error) {
    log(`⚠️ Could not save session to Firestore: ${error.message}`);
  }
};

const getLocalNewsSessionFromFirestore = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready, skipping session load');
      return null;
    }

    const userRef = window.firestoreService.db.collection('users')
      .doc(window.firestoreService.currentUser.uid)
      .collection('localNewsStations')
      .doc('currentSession');

    const doc = await userRef.get();
    
    if (doc.exists) {
      const data = doc.data();
      
      // Only use if same day
      if (data.date === new Date().toDateString()) {
        log(`📍 Found session from Firestore: card ${data.lastCardIndex}, ${data.completedCards.length} completed`);
        return data;
      } else {
        log('🗑️ Old session found in Firestore, starting fresh');
        await clearLocalNewsSessionFromFirestore();
      }
    }
  } catch (error) {
    log(`⚠️ Could not load session from Firestore: ${error.message}`);
  }
  return null;
};

const clearLocalNewsSessionFromFirestore = async () => {
  try {
    if (!window.firestoreService || !window.firestoreService.isReady()) {
      log('⚠️ Firestore not ready, skipping session clear');
      return;
    }

    const userRef = window.firestoreService.db.collection('users')
      .doc(window.firestoreService.currentUser.uid)
      .collection('localNewsStations')
      .doc('currentSession');

    await userRef.delete();
    log('🗑️ Session cleared from Firestore');
  } catch (error) {
    log(`⚠️ Could not clear session from Firestore: ${error.message}`);
  }
};
  const changeSpeed = () => {
    const speeds = [1.0, 1.15, 1.25, 1.5];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
  };

return (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.25s ease'
  }}>
    <div style={{
      width: '100%',
      height: '92vh',
      backgroundColor: '#1a1a1a',
      borderRadius: '28px 28px 0 0',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderTop: '2px solid #8d8d8dff',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Gradient background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #ea6671ff 0%, #a2714bff 100%)',
        opacity: 0.3,
        zIndex: 0
      }} />
      
      {/* Header */}
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(255, 255, 255, 0.2)'
          }}>
            <Radio size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#fff',
              textAlign: 'left',
              marginBottom: '2px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }}>
              {userLocation.city} News
            </div>
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'left',
              background: currentStatus 
                ? 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)'
                : 'none',
              backgroundSize: currentStatus ? '200% 100%' : 'auto',
              backgroundClip: currentStatus ? 'text' : 'initial',
              WebkitBackgroundClip: currentStatus ? 'text' : 'initial',
              WebkitTextFillColor: currentStatus ? 'transparent' : 'rgba(255, 255, 255, 0.7)',
              animation: currentStatus ? 'textShimmer 3s linear infinite' : 'none'
            }}>
              {currentStatus || 'Live news updates'}
            </div>
          </div>
        </div>
        
        <button
          onClick={handleClose}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
        >
          <X size={18} color="#fff" />
        </button>
      </div>

{/* Content Area - Scrollable transcript area */}
<div 
  ref={transcriptEndRef}
  style={{
    flex: 1,
    position: 'relative',
    zIndex: 2,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px',
    paddingBottom: '280px', // ✅ CHANGED from '24px'
    display: 'flex',
    flexDirection: 'column'
  }}
>
  {/* Initial State */}
  {!isPlaying && (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '8px',
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }}>
          Tap play to start local news station
        </h3>
        <p style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.7)',
          lineHeight: '1.5'
        }}>
          Real-time updates from {userLocation.city}, {userLocation.state}
        </p>
      </div>
    </div>
  )}

{/* Paused State - Click Play to Resume */}
{isPaused && isPlaying && (
  <div style={{
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.6s ease'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <Pause size={40} color="#fff" />
      </div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '8px',
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
      }}>
        Paused
      </h3>
      <p style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        lineHeight: '1.5'
      }}>
        Click play to resume
      </p>
    </div>
  </div>
)}
{/* SHIMMER LOADING - While searching for news */}
{isPlaying && isLoadingCards && !isSearchingComplete && (
  <div style={{ 
    padding: '24px', 
    paddingBottom: '160px',
    animation: 'fadeInUp 0.5s ease-out'
  }}>
    {/* Loading Header - LEFT ALIGNED */}
    <div style={{
      textAlign: 'left',
      marginBottom: '40px'
    }}>
      <div style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#fff',
        marginBottom: '8px',
        background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.4) 100%)',
        backgroundSize: '200% auto',
        animation: 'shimmerText 2.5s ease-in-out infinite',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        display: 'inline-block'
      }}>
        Gathering {userLocation.city} news...
      </div>
    </div>
    
    {/* Shimmer Lines - LEFT ALIGNED */}
    <div style={{
      width: '100%',
      maxWidth: '600px'
    }}>
      {/* First host block */}
      <div style={{
        marginBottom: '28px',
        animation: 'fadeInUp 0.5s ease-out 0.1s both'
      }}>
        {/* Host label shimmer */}
        <div style={{
          width: '60px',
          height: '12px',
          background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.3) 100%)',
          backgroundSize: '200% auto',
          animation: 'shimmer 2s ease-in-out infinite',
          borderRadius: '4px',
          marginBottom: '10px'
        }}/>
        
        {/* Text lines */}
        {[85, 92, 78].map((width, idx) => (
          <div
            key={idx}
            style={{
              height: '20px',
              width: `${width}%`,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)',
              backgroundSize: '200% auto',
              animation: 'shimmer 2s ease-in-out infinite',
              borderRadius: '6px',
              marginBottom: '12px',
              animationDelay: `${idx * 0.2}s`
            }}
          />
        ))}
      </div>
      
      {/* Second host block */}
      <div style={{
        animation: 'fadeInUp 0.5s ease-out 0.2s both'
      }}>
        {/* Host label shimmer */}
        <div style={{
          width: '70px',
          height: '12px',
          background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.3) 100%)',
          backgroundSize: '200% auto',
          animation: 'shimmer 2s ease-in-out infinite',
          borderRadius: '4px',
          marginBottom: '10px',
          animationDelay: '0.6s'
        }}/>
        
        {/* Text lines */}
        {[90, 80, 88].map((width, idx) => (
          <div
            key={`second-${idx}`}
            style={{
              height: '20px',
              width: `${width}%`,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)',
              backgroundSize: '200% auto',
              animation: 'shimmer 2s ease-in-out infinite',
              borderRadius: '6px',
              marginBottom: '12px',
              animationDelay: `${(idx + 3) * 0.2}s`
            }}
          />
        ))}
      </div>
    </div>
  </div>
)}
{/* Transcript - ONLY show when playing and NOT paused */}
{transcript.length > 0 && !isPaused && isPlaying && (
  <div style={{ marginBottom: '32px' }}>
    {transcript.slice(-1).map((item) => {
      const isSearching = item.text.includes('Searching') || 
                         item.text.includes('One sec') || 
                         item.text.includes('Gathering');
      
      return (
        <div key={item.timestamp} style={{
          marginBottom: '20px',
          textAlign: 'left',
          animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ffffff',
            marginTop: '8px',
            flexShrink: 0,
            animation: 'pulseGlow 1.5s infinite'
          }} />
          <div style={{
            fontSize: '20px',
            fontWeight: '500',
            color: '#ffffff',
            lineHeight: '1.6',
            flex: 1,
            textAlign: 'left',
            ...(isSearching && {
              background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'textShimmer 3s linear infinite'
            })
          }}>
            {item.text}
          </div>
        </div>
      );
    })}
  </div>
)}
</div>

      {/* Cycle indicator */}
      {isPlaying && currentCycle > 0 && (
        <div style={{
          padding: '8px 24px',
          textAlign: 'center',
          fontSize: '11px',
          color: 'rgba(255, 255, 255, 0.5)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          zIndex: 2
        }}>
          News cycle {currentCycle}
        </div>
      )}

{/* News Cards - FIXED at bottom, separate from scrolling content */}
{/* News Cards - FIXED at bottom, separate from scrolling content */}
{isPlaying && isSearchingComplete && (newsCards.length > 0 || isLoadingCards) && (
  <div style={{
    position: 'fixed',
    bottom: '110px',
    left: 0,
    right: 0,
    zIndex: 25,
    pointerEvents: 'none',
    paddingTop: '20px'
  }}>
    <div 
      ref={newsCardsRef}
      style={{
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '15px',
        paddingBottom: '34px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        scrollBehavior: 'smooth',
        pointerEvents: 'auto'
      }}
      className="news-cards-scroll"
    >
{/* News Cards - FIXED at bottom, separate from scrolling content */}
{isPlaying && isSearchingComplete && (newsCards.length > 0 || isLoadingCards) && (
  <div style={{
    position: 'fixed',
    bottom: '110px',
    left: 0,
    right: 0,
    zIndex: 25,
    pointerEvents: 'none',
    paddingTop: '20px'
  }}>
    <div 
      ref={newsCardsRef}
      style={{
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '15px',
        paddingBottom: '34px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        scrollBehavior: 'smooth',
        pointerEvents: 'auto'
      }}
      className="news-cards-scroll"
    >
      {isLoadingCards ? (
        // Skeleton cards
        [...Array(5)].map((_, idx) => (
          <div
            key={`skeleton-${idx}`}
            style={{
              minWidth: '130px',
              maxWidth: '130px',
              height: '160px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              overflow: 'hidden',
              position: 'relative',
              flexShrink: 0,
              animation: 'fadeInScale 0.4s ease forwards',
              animationDelay: `${idx * 0.08}s`,
              opacity: 0
            }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent)',
              animation: 'shimmer 2s infinite',
              zIndex: 1
            }} />
            
            <div style={{
              width: '100%',
              height: '90px',
              background: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '12px 12px 0 0'
            }} />
            
            <div style={{
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}>
              <div style={{
                width: '40%',
                height: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '4px'
              }} />
              <div style={{
                width: '90%',
                height: '10px',
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '4px'
              }} />
              <div style={{
                width: '70%',
                height: '10px',
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '4px'
              }} />
            </div>
          </div>
        ))
      ) : (
        <>
          {/* Actual news cards */}
          {newsCards.map((card, idx) => {
            const isCurrentlyPlaying = card.isActive;
            const isPastCard = card.isReady && !card.isActive;
            const isFutureCard = !card.isReady;
            
            return (
              <div
                key={card.id}
                style={{
                  minWidth: '130px',
                  maxWidth: '130px',
                  height: '160px',
                  borderRadius: '12px',
                  background: isCurrentlyPlaying 
                    ? 'rgba(255, 255, 255, 0.18)'
                    : 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  border: isCurrentlyPlaying 
                    ? '2px solid rgba(255, 255, 255, 0.95)' 
                    : '1px solid rgba(255, 255, 255, 0.15)',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: isCurrentlyPlaying ? 1 : (isPastCard ? 0.4 : 0.25),
                  transform: isCurrentlyPlaying ? 'scale(1.08)' : 'scale(1)',
                  boxShadow: isCurrentlyPlaying 
                    ? '0 12px 32px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(255, 255, 255, 0.25)' 
                    : '0 4px 16px rgba(0, 0, 0, 0.2)',
                  position: 'relative',
                  cursor: 'pointer',
                  flexShrink: 0,
                  filter: isCurrentlyPlaying ? 'brightness(1.15) saturate(1.1)' : 'brightness(0.9)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleArticleClick(card);
                }}
              >
                <div style={{
                  width: '100%',
                  height: '90px',
                  overflow: 'hidden',
                  position: 'relative',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <img 
                    src={card.image}
                    alt={card.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease',
                      transform: isCurrentlyPlaying ? 'scale(1.05)' : 'scale(1)'
                    }}
                  />
                  
                  {isCurrentlyPlaying && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      padding: '8px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        animation: 'pulseText 1.5s infinite'
                      }}>
                        <Radio size={10} strokeWidth={2.5} />
                        <span>Now Playing</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    fontSize: '8px',
                    fontWeight: '700',
                    color: isCurrentlyPlaying ? '#93c5fd' : 'rgba(147, 197, 253, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    textAlign: 'left'
                  }}>
                    {card.source}
                  </div>
                  
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: isCurrentlyPlaying ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                    lineHeight: '1.3',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {card.title}
                  </div>
                </div>
                
                {isCurrentlyPlaying && (
                  <div style={{
                    position: 'absolute',
                    top: -3,
                    left: -3,
                    right: -3,
                    bottom: -3,
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.6), rgba(147,197,253,0.4), rgba(255,255,255,0.6))',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite',
                    zIndex: -1
                  }} />
                )}
              </div>
            );
          })}
          
          {/* ✅ REFRESH CARD - Always shows after last card */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshClick();
            }}
            style={{
              minWidth: '130px',
              maxWidth: '130px',
              height: '160px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(20px)',
              border: '2px dashed rgba(255, 255, 255, 0.4)',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <RefreshCw size={20} color="#fff" />
            </div>
            
            <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#fff',
              textAlign: 'center',
              padding: '0 10px'
            }}>
              Search Fresh News
            </div>
            
            <div style={{
              fontSize: '8px',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.6)',
              textAlign: 'center'
            }}>
              Tap to refresh
            </div>
          </div>
        </>
      )}
    </div>
  </div>
)}
    </div>
  </div>
)}
{/* Controls - Fixed at bottom */}
<div style={{
  background: 'rgba(244, 251, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  padding: '16px 20px 24px',
  borderTop: '1px solid rgba(255, 255, 255, 0.56)',
  borderRadius: '42px 42px 0 0',
  position: 'relative',
  zIndex: 30
}}>
  {isPlaying && currentCycle > 0 && (
    <div style={{
      textAlign: 'center',
      marginBottom: '12px',
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)'
    }}>
      News cycle {currentCycle}
    </div>
  )}
  
  <div style={{
    background: '#ffffff',
    borderRadius: '32px',
    padding: '8px 20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(169, 177, 185, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px'
  }}>
    <button
      onClick={changeSpeed}
      style={{
        padding: '12px 16px',
        borderRadius: '20px',
        background: '#f3f4f6',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        color: '#000',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
      onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
    >
      {playbackSpeed}x
    </button>

<button
  onClick={handlePrevCard}
  disabled={currentCardIndex === 0 || !isPlaying}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex === 0 || !isPlaying ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex === 0 || !isPlaying ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex === 0 || !isPlaying ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex > 0 && isPlaying) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex > 0 && isPlaying) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>

    <button
      onClick={togglePlayPause}
      disabled={isGenerating}
      style={{
        width: '58px',
        height: '58px',
        borderRadius: '50%',
        background: isGenerating ? '#6b7280' : '#000',
        border: 'none',
        cursor: isGenerating ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        opacity: isGenerating ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (!isGenerating) {
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isGenerating) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      {isPaused || !isPlaying ? (
        <Play size={24} color="white" fill="white" />
      ) : (
        <Pause size={24} color="white" fill="white" />
      )}
    </button>

<button
  onClick={handleNextCard}
  disabled={currentCardIndex >= newsCards.length - 1 || !isPlaying}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex >= newsCards.length - 1 || !isPlaying ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex >= newsCards.length - 1 || !isPlaying ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex >= newsCards.length - 1 || !isPlaying ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M13 5l7 7-7 7M4 5l7 7-7 7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>
  </div>
</div>
    </div>
{/* Article Confirmation Modal */}
{showArticleConfirm && selectedArticle && (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 40,
    animation: 'fadeIn 0.3s ease'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '24px',
      padding: '32px',
      maxWidth: '400px',
      margin: '0 20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      textAlign: 'center'
    }}>
      <div style={{
        width: '100%',
        height: '120px',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '20px'
      }}>
        <img 
          src={selectedArticle.image}
          alt={selectedArticle.fullTitle}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
      
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '8px',
        lineHeight: '1.4'
      }}>
        {selectedArticle.fullTitle}
      </h3>
      
      <p style={{
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginBottom: '4px'
      }}>
        {selectedArticle.source}
      </p>
      
      <p style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: '24px',
        lineHeight: '1.5'
      }}>
        Would you like to read the full article?
      </p>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center'
      }}>
        <button
          onClick={cancelArticle}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          Cancel
        </button>
        
        <button
          onClick={confirmArticle}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: '#ffffff',
            border: 'none',
            color: '#000000',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
        >
          Continue
        </button>
      </div>
    </div>
  </div>
)}
{/* Refresh Confirmation Modal */}
{showRefreshConfirm && (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 40,
    animation: 'fadeIn 0.3s ease'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '24px',
      padding: '32px',
      maxWidth: '400px',
      margin: '0 20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      textAlign: 'center'
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px'
      }}>
        <RefreshCw size={28} color="#fff" />
      </div>
      
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '8px',
        lineHeight: '1.4'
      }}>
        Search for Fresh News?
      </h3>
      
      <p style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: '24px',
        lineHeight: '1.5'
      }}>
        We'll search for the latest local stories and start a new cycle.
      </p>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center'
      }}>
        <button
          onClick={cancelRefresh}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          Cancel
        </button>
        
        <button
          onClick={confirmRefresh}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: '#ffffff',
            border: 'none',
            color: '#000000',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
        >
          Continue
        </button>
      </div>
    </div>
  </div>
)}
    <style>{`
    @keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes pulseGlow {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.2); }
      }
      
      @keyframes textShimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      
      @keyframes fadeInScale {
        0% {
          opacity: 0;
          transform: scale(0.9);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      @keyframes pulseText {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.8; }
      }

      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      
      .news-cards-scroll::-webkit-scrollbar {
        display: none;
      }
      
@keyframes shimmer {
  0% {
    background-position: -200% 0;
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    background-position: 200% 0;
    opacity: 0.6;
  }
}

@keyframes shimmerText {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


      .news-cards-scroll {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
        @keyframes slideInFromLeft {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}
    `}</style>
  </div>
);
};

export default LocalNewsStation;